import { isQaFailureRunStatus } from "@enrein/shared-contracts/studio/derive-studio-project-lifecycle";

/** User-facing copy when inline/blob preview HTML is missing or not display-safe. */
export const PREVIEW_UNAVAILABLE_MESSAGE =
  "Preview is not available yet. Rebuild or check quality gate reasons.";

/** User-facing copy when the latest run failed quality gate. */
export const PREVIEW_QA_FAILED_MESSAGE =
  "Generation did not pass quality gate. Review facts and rebuild before opening preview.";

/** Minimum raw HTML length before treating preview as displayable (aligned with lane-e smoke). */
export const PREVIEW_HTML_MIN_BYTES = 100;

export interface PreviewOpenGuardInput {
  readonly previewHtml: string | null | undefined;
  readonly previewUrl: string | null | undefined;
  readonly previewVersionId: string | null | undefined;
  readonly qaFailure: boolean;
  readonly latestRunStatus: string | null | undefined;
}

export interface PreviewOpenGuardDebugEvidence {
  readonly previewHtmlLength: number;
  readonly hasPreviewVersionId: boolean;
  readonly latestRunStatus: string | null;
  readonly qaFailure: boolean;
  readonly hasPreviewUrl: boolean;
}

function stripHtmlToText(html: string): string {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function previewBodyContent(html: string): string {
  const bodyMatch = /<body[^>]*>([\s\S]*?)<\/body>/i.exec(html);
  return stripHtmlToText(bodyMatch?.[1] ?? html);
}

export function isPreviewHtmlDisplaySafe(html: string | null | undefined): boolean {
  if (html == null) return false;
  const trimmed = html.trim();
  if (trimmed.length < PREVIEW_HTML_MIN_BYTES) return false;
  return previewBodyContent(trimmed).length > 0;
}

export function getPreviewOpenBlockReason(input: PreviewOpenGuardInput): string | null {
  if (input.qaFailure || isQaFailureRunStatus(input.latestRunStatus)) {
    return PREVIEW_QA_FAILED_MESSAGE;
  }
  if (input.previewUrl) {
    return null;
  }
  if (!isPreviewHtmlDisplaySafe(input.previewHtml)) {
    return PREVIEW_UNAVAILABLE_MESSAGE;
  }
  return null;
}

export function isPreviewOpenAllowed(input: PreviewOpenGuardInput): boolean {
  return getPreviewOpenBlockReason(input) === null;
}

export function buildPreviewOpenGuardDebugEvidence(
  input: PreviewOpenGuardInput,
): PreviewOpenGuardDebugEvidence {
  return {
    previewHtmlLength: input.previewHtml?.length ?? 0,
    hasPreviewVersionId: Boolean(input.previewVersionId),
    latestRunStatus: input.latestRunStatus ?? null,
    qaFailure: input.qaFailure,
    hasPreviewUrl: Boolean(input.previewUrl),
  };
}

export function logPreviewOpenGuardDebug(context: string, input: PreviewOpenGuardInput): void {
  // eslint-disable-next-line no-console -- intentional dev diagnostic for preview-open guard
  console.debug(`[preview-open-guard] ${context}`, buildPreviewOpenGuardDebugEvidence(input));
}

export interface PreviewOpenChildDocument {
  readonly open?: () => void;
  readonly write?: (html: string) => void;
  readonly close?: () => void;
}

export interface PreviewOpenChildWindow {
  opener: unknown;
  document?: PreviewOpenChildDocument | null;
  location?: { href?: string } | null;
}

export interface PreviewOpenWindowLike {
  open: Window["open"];
  setTimeout: Window["setTimeout"];
  URL: Pick<typeof URL, "createObjectURL" | "revokeObjectURL">;
  /**
   * Origin of the Studio document. Used to inject a `<base href>` into the
   * preview document so relative asset URLs resolve the same way they do in
   * the in-app preview (which renders inside the Studio host document).
   */
  location?: { origin?: string | null } | null;
}

/**
 * A standalone preview document has no Studio path component, so relative asset
 * URLs (`href="site.css"`, `src="images/hero.png"`, `url(fonts/x.woff2)`) fail
 * to resolve and the page renders unstyled — often visually blank even though
 * the DOM still contains text. The in-app preview does not hit this because it
 * is injected into the Studio host document at the deployed origin.
 *
 * Injecting `<base href="{origin}/">` restores that origin-relative resolution
 * so the popped-out preview matches the in-app render. We do not override an
 * author-provided `<base>`.
 */
export function withPreviewBaseHref(html: string, origin: string | null | undefined): string {
  if (!origin) return html;
  if (/<base\b[^>]*>/i.test(html)) return html;
  const baseTag = `<base href="${origin.replace(/\/+$/, "")}/">`;
  const headOpen = /<head\b[^>]*>/i.exec(html);
  if (headOpen) {
    const at = headOpen.index + headOpen[0].length;
    return `${html.slice(0, at)}${baseTag}${html.slice(at)}`;
  }
  const htmlOpen = /<html\b[^>]*>/i.exec(html);
  if (htmlOpen) {
    const at = htmlOpen.index + htmlOpen[0].length;
    return `${html.slice(0, at)}<head>${baseTag}</head>${html.slice(at)}`;
  }
  return `<!doctype html><html><head>${baseTag}</head><body>${html}</body></html>`;
}

/**
 * Write preview HTML into an already-opened child window.
 * Preferred over `blob:` navigation — Cursor/Electron embedded browsers often
 * fail blob tabs with ERR_FILE_NOT_FOUND (-6).
 */
export function writePreviewHtmlToWindow(
  child: PreviewOpenChildWindow,
  html: string,
): boolean {
  try {
    const doc = child.document;
    if (!doc || typeof doc.open !== "function" || typeof doc.write !== "function" || typeof doc.close !== "function") {
      return false;
    }
    doc.open();
    doc.write(html);
    doc.close();
    return true;
  } catch {
    return false;
  }
}

function severOpener(child: PreviewOpenChildWindow): void {
  try {
    child.opener = null;
  } catch {
    // Cross-origin access can throw; defensive only.
  }
}

/**
 * Open the preview HTML in a new tab.
 *
 * Prefer `about:blank` + `document.write` (Cursor-safe). Fall back to a `blob:`
 * URL when the child window cannot accept a document write.
 *
 * Returns `false` when the HTML is not display-safe OR when the browser refused
 * to open the tab (popup blocked). We intentionally do NOT pass `noopener`:
 * `noopener` makes `window.open` return `null` unconditionally, which hides
 * popup-block failures and leaves no handle to the child.
 */
export function openBlobPreviewInNewTab(
  html: string,
  windowLike: PreviewOpenWindowLike,
): boolean {
  if (!isPreviewHtmlDisplaySafe(html)) {
    return false;
  }
  const documentHtml = withPreviewBaseHref(html, windowLike.location?.origin ?? null);
  const opened = windowLike.open("about:blank", "_blank") as PreviewOpenChildWindow | null;
  if (!opened) {
    return false;
  }

  if (writePreviewHtmlToWindow(opened, documentHtml)) {
    severOpener(opened);
    return true;
  }

  // Fallback for environments that cannot document.write into the child.
  const blob = new Blob([documentHtml], { type: "text/html" });
  const url = windowLike.URL.createObjectURL(blob);
  try {
    if (opened.location && typeof opened.location === "object") {
      opened.location.href = url;
    } else {
      const navigated = windowLike.open(url, "_blank") as PreviewOpenChildWindow | null;
      if (!navigated) {
        windowLike.URL.revokeObjectURL(url);
        return false;
      }
      severOpener(navigated);
      windowLike.setTimeout(() => windowLike.URL.revokeObjectURL(url), 60_000);
      return true;
    }
  } catch {
    windowLike.URL.revokeObjectURL(url);
    return false;
  }
  severOpener(opened);
  windowLike.setTimeout(() => windowLike.URL.revokeObjectURL(url), 60_000);
  return true;
}
